/**
 * 管理员提示词管理页面
 * 单列卡片布局，每张卡片对应一个提示词（system + user 两个 textarea）
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { adminApi } from '../../services/adminApi';
import { toast, confirm } from '../../store/toastStore';

interface PromptMeta {
  key: string;
  name: string;
  category: string;
  version: number;
  content_system: string;
  content_user: string | null;
  has_user: boolean;
  variables_user: string[];
  updated_at: string | null;
}

interface VersionItem {
  version: number;
  content_system: string;
  content_user: string | null;
  is_active: boolean;
  updated_at: string | null;
}

type DocType = 'bid' | 'proposal';

const PROMPT_CATEGORY_LABELS: Record<string, string> = {
  document_analysis: '文档分析',
  outline: '大纲生成',
  content: '内容生成',
};

const AdminPrompts: React.FC = () => {
  const [docType, setDocType] = useState<DocType>('bid');
  const [prompts, setPrompts] = useState<PromptMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [historyModal, setHistoryModal] = useState<{ key: string; name: string } | null>(null);
  const [versions, setVersions] = useState<VersionItem[]>([]);
  const [previewVersion, setPreviewVersion] = useState<VersionItem | null>(null);
  const [activeKey, setActiveKey] = useState<string>('');
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const initialSnapshot = useRef<Record<string, { system: string; user: string | null }>>({});

  const loadPrompts = useCallback(async (dt: DocType) => {
    setLoading(true);
    try {
      const res = await adminApi.getPrompts(dt);
      const data = res.data as PromptMeta[];
      setPrompts(data);
      // 保存初始快照用于对比是否修改
      const snap: Record<string, { system: string; user: string | null }> = {};
      data.forEach(p => { snap[p.key] = { system: p.content_system, user: p.content_user }; });
      initialSnapshot.current = snap;
    } catch (e) {
      console.error('加载提示词失败', e);
      toast.error('加载提示词失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPrompts(docType);
  }, [docType, loadPrompts]);

  const handleSave = async (p: PromptMeta) => {
    // 检查是否真的修改了
    const snap = initialSnapshot.current[p.key];
    if (snap && snap.system === p.content_system && snap.user === p.content_user) {
      toast.warning('内容未发生变化，无需保存新版本');
      return;
    }
    setSaving(p.key);
    try {
      const res = await adminApi.updatePrompt(p.key, {
        content_system: p.content_system,
        content_user: p.content_user,
      });
      // 只更新该卡片版本号+快照，不触发全量重渲染导致滚动跳顶
      const newVersion = res.data.version;
      setPrompts(prev => prev.map(item =>
        item.key === p.key ? { ...item, version: newVersion } : item
      ));
      initialSnapshot.current[p.key] = { system: p.content_system, user: p.content_user };
      toast.success('已生效');
    } catch (e) {
      console.error('保存失败', e);
      toast.error('保存失败');
    } finally {
      setSaving(null);
    }
  };

  const updateContent = (key: string, field: 'content_system' | 'content_user', value: string) => {
    setPrompts(prev => prev.map(p => p.key === key ? { ...p, [field]: value } : p));
  };

  const openHistory = async (key: string, name: string) => {
    setHistoryModal({ key, name });
    try {
      const res = await adminApi.getPromptVersions(key);
      setVersions(res.data as VersionItem[]);
    } catch (e) {
      toast.error('加载版本历史失败');
      setVersions([]);
    }
  };

  const handleRevert = async (key: string, version: number) => {
    try {
      await adminApi.revertPrompt(key, version);
      toast.success('已设为当前版本');
      setHistoryModal(null);
      await loadPrompts(docType);
    } catch (e) {
      toast.error('回退失败');
    }
  };

  const groupedPrompts = prompts.reduce<Record<string, PromptMeta[]>>((acc, p) => {
    const cat = p.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  const CATEGORY_ORDER = ['document_analysis', 'outline', 'content'];
  const categories = CATEGORY_ORDER.filter(k => groupedPrompts[k]);

  const shortName = (fullName: string) => {
    const prefix = docType === 'bid' ? '标书·' : '方案·';
    return fullName.startsWith(prefix) ? fullName.slice(prefix.length) : fullName;
  };

  // Scrollspy: 滚动时自动高亮当前可见的提示词
  useEffect(() => {
    if (loading || prompts.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) {
          setActiveKey(visible[0].target.id);
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );
    prompts.forEach(p => {
      const el = document.getElementById(p.key);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [loading, prompts]);

  return (
    <div className="flex gap-6">
      {/* 左侧导航侧边栏 */}
      {!loading && prompts.length > 0 && (
        <div className="w-[220px] shrink-0 hidden lg:block">
          <div className="sticky top-4 space-y-5 max-h-[calc(100vh-6rem)] overflow-y-auto">
            {categories.map(cat => (
              <div key={cat}>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                  {PROMPT_CATEGORY_LABELS[cat] || cat}
                  <span className="ml-1 text-muted-foreground font-normal">({groupedPrompts[cat].length})</span>
                </div>
                <div className="space-y-0.5">
                  {groupedPrompts[cat].map(p => (
                    <button
                      key={p.key}
                      onClick={() => document.getElementById(p.key)?.scrollIntoView({ behavior: 'smooth' })}
                      className={`w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${
                        activeKey === p.key
                          ? 'bg-primary/10 text-primary font-medium border-l-[3px] border-primary -ml-[3px]'
                          : 'text-foreground hover:text-foreground hover:bg-accent border-l-[3px] border-transparent -ml-[3px]'
                      }`}
                    >
                      {shortName(p.name)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 右侧内容区 */}
      <div ref={scrollContainerRef} className="flex-1 min-w-0 max-w-4xl">
        {/* 标题和标签切换 */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-foreground">提示词管理</h1>
          <div className="flex space-x-2 bg-accent rounded-lg p-1">
            <button
              onClick={() => setDocType('bid')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                docType === 'bid' ? 'bg-card text-primary shadow-sm' : 'text-foreground hover:text-foreground'
              }`}
            >
              标书提示词
            </button>
            <button
              onClick={() => setDocType('proposal')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                docType === 'proposal' ? 'bg-card text-primary shadow-sm' : 'text-foreground hover:text-foreground'
              }`}
            >
              方案提示词
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">加载中...</div>
        ) : prompts.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            {docType === 'proposal' ? '方案提示词为空，请先编辑保存' : '暂无可用的提示词'}
          </div>
        ) : (
          categories.map(cat => (
            <div key={cat} className="mb-8">
              <h2 className="text-lg font-semibold text-foreground mb-3 px-1">
                {PROMPT_CATEGORY_LABELS[cat] || cat}
              </h2>
              <div className="space-y-4">
                {groupedPrompts[cat].map(p => (
                  <div key={p.key} id={p.key}>
                    <PromptCard
                      prompt={p}
                      onSave={handleSave}
                      onUpdate={updateContent}
                      onHistory={() => openHistory(p.key, p.name)}
                      saving={saving === p.key}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))
        )}

        {/* 版本历史弹窗 */}
        {historyModal && (
          <div className="fixed inset-0 bg-black/40 flex items-start justify-center pt-20 z-50" onClick={() => setHistoryModal(null)}>
            <div className="bg-card rounded-xl shadow-xl w-full max-w-2xl max-h-[70vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                <h3 className="text-lg font-semibold text-foreground">版本历史 · {historyModal.name}</h3>
                <button onClick={() => setHistoryModal(null)} className="text-muted-foreground hover:text-foreground text-xl">&times;</button>
              </div>
              <div className="p-6">
                {versions.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">暂无版本历史</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b border-border">
                        <th className="pb-2 font-medium">版本</th>
                        <th className="pb-2 font-medium">更新时间</th>
                        <th className="pb-2 font-medium text-right">操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {versions.map(v => (
                        <tr key={v.version} className="border-b border-border">
                          <td className="py-3">
                            <span className="font-mono">v{v.version}</span>
                            {v.is_active && <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">当前生效</span>}
                          </td>
                          <td className="py-3 text-muted-foreground">{v.updated_at ? new Date(v.updated_at).toLocaleString() : '-'}</td>
                          <td className="py-3 text-right space-x-2">
                            <button onClick={() => setPreviewVersion(v)} className="text-primary hover:text-primary text-sm">预览</button>
                            {!v.is_active && (
                              <button onClick={() => handleRevert(historyModal.key, v.version)} className="text-orange-600 hover:text-orange-800 text-sm">设为当前</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 预览弹窗 */}
        {previewVersion && (
          <div className="fixed inset-0 bg-black/40 flex items-start justify-center pt-20 z-50" onClick={() => setPreviewVersion(null)}>
            <div className="bg-card rounded-xl shadow-xl w-full max-w-3xl max-h-[70vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                <h3 className="text-lg font-semibold text-foreground">预览 · v{previewVersion.version}</h3>
                <button onClick={() => setPreviewVersion(null)} className="text-muted-foreground hover:text-foreground text-xl">&times;</button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">system</div>
                  <pre className="bg-accent p-4 rounded-lg text-sm whitespace-pre-wrap max-h-60 overflow-y-auto">{previewVersion.content_system}</pre>
                </div>
                {previewVersion.content_user && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">user</div>
                    <pre className="bg-accent p-4 rounded-lg text-sm whitespace-pre-wrap max-h-60 overflow-y-auto">{previewVersion.content_user}</pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── 单张提示词卡片 ──

interface PromptCardProps {
  prompt: PromptMeta;
  onSave: (p: PromptMeta) => void;
  onUpdate: (key: string, field: 'content_system' | 'content_user', value: string) => void;
  onHistory: () => void;
  saving: boolean;
}

const PromptCard: React.FC<PromptCardProps> = ({ prompt: p, onSave, onUpdate, onHistory, saving }) => {
  const systemRef = useRef<HTMLTextAreaElement | null>(null);
  const userRef = useRef<HTMLTextAreaElement | null>(null);
  const [copiedVar, setCopiedVar] = useState<string | null>(null);

  // 变量中文注释：提示管理员每个变量的含义
  const varAnnotations: Record<string, string> = {
    project_info: '项目信息（名称、编号、采购方式等）',
    context_info: '上下文信息（上级章节概要）',
    chapter_id: '章节编号',
    chapter_title: '章节标题',
    chapter_description: '章节描述',
    children_info: '子章节列表及其描述',
    project_context: '项目背景概述',
    overview: '项目概述',
    requirements: '技术评分要求',
    old_outline: '已有目录结构（用户上传的旧方案）',
    file_content: '文档原文内容（用户上传的文件文本）',
    chapter_info: '章节编号和标题',
    references: '系统搜索到的参考资料',
    originality_hint: '重新生成时强调原创性的提示',
  };

  const handleCopyVar = async (varName: string) => {
    const text = `{${varName}}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedVar(varName);
      setTimeout(() => setCopiedVar(null), 1500);
    } catch {
      // fallback: select text method
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedVar(varName);
      setTimeout(() => setCopiedVar(null), 1500);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* 标题栏 */}
      <div className="flex items-center justify-between px-5 py-3 bg-accent border-b border-border">
        <div className="flex items-center gap-3 min-w-0">
          <span className="font-medium text-foreground truncate">{p.name}</span>
          <span className="text-xs text-muted-foreground font-mono truncate hidden sm:inline">{p.key}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-xs text-muted-foreground">v{p.version}</span>
          {p.updated_at && (
            <span className="text-xs text-muted-foreground hidden md:inline">
              {new Date(p.updated_at).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      <div className="px-5 py-3">
        <div className="text-xs text-muted-foreground mb-1">system</div>
        <textarea
          ref={systemRef}
          value={p.content_system}
          onChange={e => onUpdate(p.key, 'content_system', e.target.value)}
          className="w-full border border-border rounded-lg p-3 text-sm font-mono leading-relaxed resize-y"
          rows={20}
          spellCheck={false}
        />
      </div>

      {/* user textarea */}
      <div className="px-5 pb-3">
        <div className="text-xs text-muted-foreground mb-1">user</div>
        <textarea
          ref={userRef}
          value={p.content_user || ''}
          onChange={e => onUpdate(p.key, 'content_user', e.target.value)}
          className="w-full border border-border rounded-lg p-3 text-sm font-mono leading-relaxed resize-y"
          rows={20}
          spellCheck={false}
        />
      </div>

      {/* 变量区 — 底部 */}
      {p.has_user && Array.isArray(p.variables_user) && p.variables_user.length > 0 && (
        <div className="px-5 pb-2">
          <span className="text-xs text-muted-foreground mr-2">可用变量（点击复制）</span>
          {p.variables_user.map(v => (
            <button
              key={v}
              onClick={() => handleCopyVar(v)}
              className={`inline-block text-xs px-2 py-0.5 rounded mr-1 mb-0.5 transition-colors relative ${
                copiedVar === v
                  ? 'bg-green-100 text-green-700'
                  : 'bg-accent text-foreground hover:bg-primary/10 hover:text-primary'
              }`}
              title={varAnnotations[v] || v}
            >
              <span>{`{${v}}`}</span>
              {copiedVar === v && (
                <span className="ml-1 text-green-600 font-medium">已复制</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* 操作栏 */}
      <div className="flex items-center justify-end gap-2 px-5 py-3 bg-accent border-t border-border">
        <button
          onClick={onHistory}
          className="px-3 py-1.5 text-sm text-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
        >
          历史
        </button>
        <button
          onClick={() => onSave(p)}
          disabled={saving}
          className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
            saving
              ? 'bg-blue-300 text-white cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {saving ? '保存中...' : '保存'}
        </button>
      </div>
    </div>
  );
};

export default AdminPrompts;
